import type {
  McpUiHostContext,
  McpUiStyles,
} from "@modelcontextprotocol/ext-apps/app-bridge";
import type { AppLaunch } from "../../api/hub/apps";
export const HTML_LIMIT = 2 * 1024 * 1024;
export const STORAGE_TOOLS = [
  "app_storage_get",
  "app_storage_set",
  "app_storage_delete",
  "app_storage_list",
] as const;
export function requireApprovedTool(launch: AppLaunch, name: string) {
  if (
    !launch.approvedTools.includes(name) &&
    !(STORAGE_TOOLS as readonly string[]).includes(name)
  ) throw new Error("应用未获准调用这个工具");
}
export function safeAppLink(raw: string, launch: AppLaunch) {
  const url = new URL(raw);
  if (
    url.protocol !== "https:" || url.username || url.password ||
    raw.length > 2048
  ) throw new Error("只允许不含账号信息的 HTTPS 链接");
  if (
    ![
      ...launch.effectiveCsp.connectDomains,
      ...launch.effectiveCsp.resourceDomains,
    ].includes(url.origin)
  ) throw new Error("此链接域名未在应用批准范围内");
  return url.href;
}
export function validateProxy(launch: AppLaunch, hostOrigin: string) {
  const url = new URL(launch.proxyUrl);
  const local = (s: string) => ["localhost", "127.0.0.1", "[::1]"].includes(s);
  if (
    url.origin !== launch.sandboxOrigin || launch.hostOrigin !== hostOrigin ||
    url.origin === hostOrigin || url.username || url.password || url.hash ||
    !["http:", "https:"].includes(url.protocol)
  ) throw new Error("沙箱地址与可信宿主配置不一致");
  if (
    url.protocol !== "https:" &&
    !(local(url.hostname) && local(new URL(hostOrigin).hostname))
  ) throw new Error("沙箱需要 HTTPS；仅本机开发允许 HTTP");
  return url.href;
}
/** 草稿下载能力：只接受单个嵌入资源，不替应用取任何 URL。 */
export function localDownload(
  contents: unknown,
): { blob: Blob; filename: string } {
  if (!Array.isArray(contents) || contents.length !== 1) {
    throw new Error("每次只能下载一个本地文件");
  }
  const item = contents[0] as {
    type?: string;
    resource?: {
      uri?: string;
      mimeType?: string;
      text?: string;
      blob?: string;
    };
  };
  if (item?.type !== "resource" || !item.resource) {
    throw new Error("不允许下载远程链接");
  }
  const r = item.resource, mime = r.mimeType ?? "text/plain";
  if (
    ![
      "text/plain",
      "text/csv",
      "application/json",
      "image/png",
      "image/jpeg",
      "application/pdf",
    ].includes(mime)
  ) throw new Error("不支持这个下载文件类型");
  let data: Uint8Array | string;
  if (typeof r.text === "string" && r.blob === undefined) data = r.text;
  else if (
    typeof r.blob === "string" && r.text === undefined &&
    /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(
      r.blob,
    ) && r.blob.length <= Math.ceil(HTML_LIMIT / 3) * 4
  ) {
    const decoded = atob(r.blob);
    data = Uint8Array.from(decoded, (c) => c.charCodeAt(0));
  } else throw new Error("文件内容格式不正确");
  const blob = new Blob([data as BlobPart], { type: mime });
  if (blob.size > HTML_LIMIT) throw new Error("下载文件超过 2 MiB");
  const filename =
    (r.uri ?? "应用导出").split(/[/?#]/).filter(Boolean).at(-1)?.replace(
      /[^\p{L}\p{N}._ -]/gu,
      "_",
    ).slice(0, 100) || "应用导出";
  return { blob, filename };
}
/** 只发设计变量，禁止透传用户、认证、任意 CSS 或业务内容。 */
export function appHostContext(
  root: HTMLElement = document.documentElement,
): McpUiHostContext {
  const style = getComputedStyle(root);
  const theme = root.dataset.lingzhiTheme === "dark" ||
      (!root.dataset.lingzhiTheme &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ? "dark"
    : "light";
  return {
    theme,
    locale: "zh-CN",
    platform: "web" as const,
    displayMode: "inline" as const,
    styles: {
      variables: {
        "--color-background-primary":
          style.getPropertyValue("--lingzhi-page-bg").trim() || "#faf9f6",
        "--color-text-primary":
          style.getPropertyValue("--lingzhi-page-fg").trim() || "#25251f",
        "--color-background-info":
          style.getPropertyValue("--lingzhi-color-accent-base").trim() ||
          "#f1c84b",
      } as McpUiStyles,
    },
  };
}
